import Link from '@components/common/Link/Link'
import Media from '@components/ui/Media/Media'

import { getProductName } from '@components/views/Product/helpersv2'
import { UmbracoLink } from '@customTypes/umbracoLink'
import { ProductDetailFragment } from '@lib/graphql/graphql'
import {
  ImageFragment,
  MediaWithCropsFragment,
} from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import { setProductScrollTimeout } from '@redux/globalSlice'
import { useAppDispatch } from '@redux/store'
import { mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import { useQueryState } from 'next-usequerystate'
import { useRouter } from 'next/router'
import { CSSProperties, FC, useEffect, useMemo, useState } from 'react'
import s from './ProductScrollBanner.module.scss'

type ProductScrollBannerProps = {
  product: ProductDetailFragment
  extraProperties: {
    media: MediaWithCropsFragment
    scrollTimeout: number
    fontStyle: string
    designerLink: UmbracoLink
  }
}

const ProductScrollBanner: FC<ProductScrollBannerProps> = ({
  product,
  extraProperties: { media, scrollTimeout, fontStyle, designerLink },
}) => {
  /*eslint-disable*/
  if (!product) return

  const router = useRouter()
  const dispatch = useAppDispatch()
  const { itemNumber } = router.query

  const [itemNumberQuery, _] = useQueryState('itemNumber', {
    defaultValue: itemNumber as string,
  })

  const variants = mapEdgesToItems(product.variants)

  const [variant, setVariant] = useState(
    variants.find((x) => x.sku === itemNumber),
  )
  useEffect(() => {
    // set the variant
    if (!itemNumberQuery) return

    setVariant(variants.find((x) => x.sku === itemNumberQuery))
  }, [itemNumberQuery])

  const mediaUrls = mapEdgesToItems(variant.media).filter(
    (media) =>
      !media.fileName.split('_')[1]?.includes('C') &&
      !media.fileName.split('_')[1]?.includes('L') &&
      !media.fileName.split('_')[1]?.includes('V') &&
      media.fileName.split('_')[1]?.length >= 2,
  )

  const mediaList = mediaUrls.map((x) => ({
    __typename: 'MediaWithCrops',
    url: generateMediaUrlV2(x.src),
    media: {
      __typename: 'Image',
      url: generateMediaUrlV2(x.src),
    } as ImageFragment,
  }))

  useEffect(() => {
    const extraTimeout = 1.5

    const elements = document.querySelectorAll(`.${s.productImageAnimation}`)
    const totalImages = elements.length
    const visibilityDuration = 0.7 // Duration each image is visible
    const totalAnimationTime = totalImages * visibilityDuration // Total time for one cycle of the animation

    // Calculate visibility percentages for keyframes
    const visiblePercentage = (visibilityDuration / totalAnimationTime) * 100
    const hiddenStartPercentage = visiblePercentage
    const hiddenEndPercentage = 100 / totalImages

    // Dynamically create keyframes
    const keyframes = `
        @keyframes showHide { 
            0%, ${hiddenStartPercentage}% { visibility: hidden; } 
            ${
              hiddenStartPercentage + 0.01
            }%, ${hiddenEndPercentage}% { visibility: visible; }
            ${hiddenEndPercentage + 0.01}%, 100% { visibility: hidden; }
        }
    `

    // Inject keyframes into the document
    const styleEl = document.createElement('style')
    document.head.appendChild(styleEl)
    styleEl.sheet.insertRule(keyframes, 0)

    // Set animation style for each element with the visibility duration
    elements.forEach((el, index) => {
      ;(el as HTMLElement).style.animation =
        `showHide ${totalAnimationTime}s ease-in-out ${
          index * visibilityDuration
        }s`
    })

    if (media && scrollTimeout) {
      dispatch(setProductScrollTimeout(scrollTimeout))
      return
    }

    if (!media) {
      dispatch(
        setProductScrollTimeout(
          mediaUrls.length * visibilityDuration + scrollTimeout,
        ),
      )
      return
    }

    if (media && media.media.__typename == 'UmbracoMediaVideo') {
      const video = document.createElement('video')
      video.src = media.url
      video.addEventListener('loadedmetadata', function () {
        dispatch(setProductScrollTimeout(video.duration))
      })

      // remove event listener
      return () => {
        video.removeEventListener('loadedmetadata', function () {
          dispatch(setProductScrollTimeout(video.duration))
        })
      }
    }
  }, [variant])

  // Prepare produuct name for scroll component title
  const productName = useMemo(
    () => getProductName(product.name),
    [product],
  )?.name.split(' ')

  const productNameFirstPart = productName.slice(0, 2).join(' ')
  const productNameLastPart = productName.slice(2).join(' ')

  return (
    <div className={s.root} id="product-scroll-banner">
      <div
        className={s.productName}
        style={
          {
            '--font-style':
              'var(--' + (fontStyle ? fontStyle : 'kingscaslon') + ')',
          } as CSSProperties
        }
      >
        {productNameFirstPart} <span>{productNameLastPart}</span>
      </div>
      <div className={s.productDetails}>
        <div className={s.productDesigner}>
          By{' '}
          <Link className={s.link} link={designerLink}>
            <span className={clsx({ [s.underlineAnimation]: designerLink })}>
              {product.vendor}
            </span>
          </Link>
        </div>
      </div>
      {!media && mediaList && (
        <div
          className={s.mediaWrapper}
          style={{ '--total-images': mediaList.length } as CSSProperties}
        >
          {mediaList.map((x, i) => (
            <Media
              key={i}
              mediaWithCrops={x as MediaWithCropsFragment}
              disableShopTheLook
              className={s.productImageAnimation}
              mediaClassName={s.productImage}
              sizes="(max-width: 768px) 100vw, 40vw"
            />
          ))}
        </div>
      )}
      {media && (
        <div className={s.mediaWrapper}>
          <Media
            mediaWithCrops={media}
            disableShopTheLook
            mediaClassName={s.productImage}
            sizes="(max-width: 768px) 100vw, 40vw"
          />
        </div>
      )}
    </div>
  )
}

export default ProductScrollBanner
